import React from 'react';
import styled from 'styled-components';

import MapWithMarker from '../common/MapWithMarker';
import {
  createRentalVehicle,
  deleteVehicleDocument,
  getRentalLocations,
  getRentalVehicles,
  getVehicleDocuments,
  updateRentalVehicle,
  uploadVehicleDocument,
} from '../../api/rental';
import SpecsEditor, {defaultSpecGroups} from './SpecsEditor';
import VehicleTelematics from './VehicleTelematics';
import Layout from './Layout';
import {
  ActionButton,
  Actions,
  Badge,
  Card,
  CardTitle,
  CheckboxRow,
  ErrorText,
  Field,
  FieldGrid,
  Grid,
  Input,
  List,
  ListButton,
  Select,
  State,
  SuccessText,
  Textarea,
} from './styles';

const statuses = {
  available: 'Доступен',
  reservation_pending: 'Ожидает оплаты',
  reserved: 'Забронирован',
  rented: 'В аренде',
  inspection: 'На осмотре',
  maintenance: 'Обслуживание',
};

const blankVehicle = {
  brand: '',
  model: '',
  year: new Date().getFullYear(),
  color: '',
  license_plate: '',
  transmission: 'automatic',
  fuel_type: 'petrol',
  fuel_level: 100,
  seats: 5,
  daily_rate_rub: '',
  description: '',
  coordinates: {lat: 55.751244, lng: 37.618423},
  hidden: false,
  status: 'available',
  photos_text: '',
  location_id: '',
  vin: '',
  body_type: '',
  drive_type: '',
  range_km: '',
  mileage_km: '',
  has_child_seat: false,
  has_booster: false,
  specs: defaultSpecGroups(),
};

const toForm = vehicle => vehicle ? ({
  ...vehicle,
  daily_rate_rub: Number(vehicle.daily_rate || 0) / 100,
  coordinates: vehicle.coordinates || blankVehicle.coordinates,
  photos_text: (vehicle.photos || []).join('\n'),
  location_id: vehicle.location_id || '',
  vin: vehicle.vin || '',
  body_type: vehicle.body_type || '',
  drive_type: vehicle.drive_type || '',
  range_km: vehicle.range_km === null || vehicle.range_km === undefined ? '' : vehicle.range_km,
  mileage_km: vehicle.mileage_km === null || vehicle.mileage_km === undefined ? '' : vehicle.mileage_km,
  has_child_seat: Boolean(vehicle.has_child_seat),
  has_booster: Boolean(vehicle.has_booster),
  specs: (vehicle.specs && vehicle.specs.length) ? vehicle.specs : defaultSpecGroups(),
}) : {...blankVehicle, coordinates: {...blankVehicle.coordinates}, specs: defaultSpecGroups()};

const toPayload = form => ({
  brand: form.brand.trim(),
  model: form.model.trim(),
  year: Number(form.year),
  color: form.color.trim(),
  license_plate: form.license_plate.trim(),
  transmission: form.transmission,
  fuel_type: form.fuel_type,
  fuel_level: Number(form.fuel_level),
  seats: Number(form.seats),
  daily_rate: Math.round(Number(form.daily_rate_rub) * 100),
  description: form.description.trim() || null,
  coordinates: {lat: Number(form.coordinates.lat), lng: Number(form.coordinates.lng)},
  hidden: Boolean(form.hidden),
  status: form.status,
  photos: form.photos_text.split('\n').map(value => value.trim()).filter(Boolean),
  location_id: form.location_id || null,
  vin: form.vin.trim() || null,
  body_type: form.body_type || null,
  drive_type: form.drive_type || null,
  range_km: form.range_km === '' ? null : Number(form.range_km),
  mileage_km: form.mileage_km === '' ? null : Number(form.mileage_km),
  has_child_seat: Boolean(form.has_child_seat),
  has_booster: Boolean(form.has_booster),
  specs: form.specs,
});

const money = kopecks => `${(Number(kopecks || 0) / 100).toLocaleString('ru-RU')} ₽/сутки`;

function FleetContent({authorization}) {
  const [vehicles, setVehicles] = React.useState([]);
  const [filter, setFilter] = React.useState('');
  const [selectedId, setSelectedId] = React.useState(() => new URLSearchParams(window.location.search).get('vehicle_id') || '');
  const [form, setForm] = React.useState(toForm(null));
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [locations, setLocations] = React.useState([]);

  React.useEffect(() => {
    if (!authorization) { return; }
    getRentalLocations(authorization)
      .then(setLocations)
      .catch(() => setLocations([]));
  }, [authorization]);

  const loadVehicles = React.useCallback((keepId = selectedId) => {
    setLoading(true);
    setError('');
    return getRentalVehicles(authorization, filter)
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setVehicles(list);
        const selected = list.find(vehicle => vehicle.id === keepId);
        if (selected) setForm(toForm(selected));
      })
      .catch(requestError => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [authorization, filter, selectedId]);

  React.useEffect(() => { loadVehicles(); }, [loadVehicles]);

  const change = (name, value) => setForm(current => ({...current, [name]: value}));
  const selectVehicle = vehicle => {
    setSelectedId(vehicle.id);
    setForm(toForm(vehicle));
    setError('');
    setMessage('');
  };
  const startCreate = () => {
    setSelectedId('');
    setForm(toForm(null));
    setError('');
    setMessage('');
  };

  const save = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = toPayload(form);
      const saved = selectedId
        ? await updateRentalVehicle(authorization, selectedId, payload)
        : await createRentalVehicle(authorization, payload);
      setSelectedId(saved.id);
      setForm(toForm(saved));
      setMessage(selectedId ? 'Автомобиль сохранён.' : 'Автомобиль создан.');
      await loadVehicles(saved.id);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid>
      <Card>
        <ListHeader>
          <CardTitle>Автомобили</CardTitle>
          <ActionButton type="button" primary onClick={startCreate}>Добавить</ActionButton>
        </ListHeader>
        <Field>
          Фильтр состояния
          <Select value={filter} onChange={event => { setFilter(event.target.value); setSelectedId(''); }}>
            <option value="">Все состояния</option>
            {Object.keys(statuses).map(status => <option key={status} value={status}>{statuses[status]}</option>)}
          </Select>
        </Field>
        <div style={{height: 16}} />
        {loading && <State>Загрузка автопарка…</State>}
        {!loading && error && !selectedId && <State error>{error}</State>}
        {!loading && !error && vehicles.length === 0 && <State>Автомобилей с таким статусом пока нет.</State>}
        {!loading && vehicles.length > 0 && (
          <List>
            {vehicles.map(vehicle => (
              <ListButton key={vehicle.id} active={vehicle.id === selectedId} onClick={() => selectVehicle(vehicle)}>
                <VehicleHeading>{vehicle.brand} {vehicle.model}</VehicleHeading>
                <VehicleMeta>{vehicle.license_plate} · {money(vehicle.daily_rate)}</VehicleMeta>
                <Badge danger={vehicle.status === 'maintenance'} success={vehicle.status === 'available'}>
                  {statuses[vehicle.status] || vehicle.status}{vehicle.hidden ? ' · скрыт' : ''}
                </Badge>
              </ListButton>
            ))}
          </List>
        )}
      </Card>

      <Card>
        <CardTitle>{selectedId ? 'Карточка автомобиля' : 'Новый автомобиль'}</CardTitle>
        <form onSubmit={save}>
          <MapBox aria-label="Карта координат автомобиля">
            <MapWithMarker
              center={{lat: Number(form.coordinates.lat), lng: Number(form.coordinates.lng)}}
              handleMarkerChange={map => change('coordinates', {lat: map.center[0], lng: map.center[1]})}
            />
          </MapBox>
          <MapHelp>Указанное оператором местоположение. Перемещайте карту: координаты фиксируются в центре маркера. Позиция устройства отображается отдельно ниже.</MapHelp>
          <FieldGrid style={{marginTop: 16}}>
            <Field>Широта *<Input required type="number" step="0.000001" value={form.coordinates.lat} onChange={event => change('coordinates', {...form.coordinates, lat: event.target.value})} /></Field>
            <Field>Долгота *<Input required type="number" step="0.000001" value={form.coordinates.lng} onChange={event => change('coordinates', {...form.coordinates, lng: event.target.value})} /></Field>
          </FieldGrid>
          <Divider />
          <FieldGrid>
            <Field>Марка *<Input required value={form.brand} onChange={event => change('brand', event.target.value)} /></Field>
            <Field>Модель *<Input required value={form.model} onChange={event => change('model', event.target.value)} /></Field>
            <Field>Год *<Input required type="number" min="1900" max="2200" value={form.year} onChange={event => change('year', event.target.value)} /></Field>
            <Field>Цвет *<Input required value={form.color} onChange={event => change('color', event.target.value)} /></Field>
            <Field>Госномер *<Input required value={form.license_plate} onChange={event => change('license_plate', event.target.value)} /></Field>
            <Field>КПП *
              <Select value={form.transmission} onChange={event => change('transmission', event.target.value)}>
                <option value="automatic">Автоматическая</option><option value="manual">Механическая</option>
              </Select>
            </Field>
            <Field>Тип топлива *
              <Select value={form.fuel_type} onChange={event => change('fuel_type', event.target.value)}>
                <option value="petrol">Бензин</option><option value="diesel">Дизель</option>
                <option value="hybrid">Гибрид</option><option value="electric">Электро</option>
              </Select>
            </Field>
            <Field>Топливо/заряд, % *<Input required type="number" min="0" max="100" value={form.fuel_level} onChange={event => change('fuel_level', event.target.value)} /></Field>
            <Field>Количество мест *<Input required type="number" min="1" max="100" value={form.seats} onChange={event => change('seats', event.target.value)} /></Field>
            <Field>Цена суток, ₽ *<Input required type="number" min="1" step="0.01" value={form.daily_rate_rub} onChange={event => change('daily_rate_rub', event.target.value)} /></Field>
            <Field>Состояние *
              <Select value={form.status} onChange={event => change('status', event.target.value)}>
                {Object.keys(statuses).map(status => <option key={status} value={status}>{statuses[status]}</option>)}
              </Select>
            </Field>
            <Field>Видимость на карте
              <CheckboxRow><input type="checkbox" checked={form.hidden} onChange={event => change('hidden', event.target.checked)} /> Скрыть от клиентов</CheckboxRow>
            </Field>
            <Field>Пункт выдачи *
              <Select value={form.location_id} onChange={event => change('location_id', event.target.value)}>
                <option value="">Не выбран</option>
                {locations.map(location => (
                  <option key={location.id} value={location.id}>{location.city} · {location.title}</option>
                ))}
              </Select>
            </Field>
            <Field>Тип кузова
              <Select value={form.body_type} onChange={event => change('body_type', event.target.value)}>
                <option value="">Не указан</option>
                <option value="sedan">Седан</option>
                <option value="hatchback">Хэтчбек</option>
                <option value="liftback">Лифтбек</option>
                <option value="coupe">Купе</option>
                <option value="crossover">Кроссовер</option>
                <option value="suv">Внедорожник</option>
                <option value="minivan">Минивэн</option>
                <option value="wagon">Универсал</option>
              </Select>
            </Field>
            <Field>Привод
              <Select value={form.drive_type} onChange={event => change('drive_type', event.target.value)}>
                <option value="">Не указан</option>
                <option value="fwd">FWD — передний</option>
                <option value="rwd">RWD — задний</option>
                <option value="awd">AWD — подключаемый полный</option>
                <option value="4wd">4WD — постоянный полный</option>
              </Select>
            </Field>
            <Field>Запас хода, км<Input type="number" min="1" value={form.range_km} onChange={event => change('range_km', event.target.value)} /></Field>
            <Field>Пробег, км<Input type="number" min="0" value={form.mileage_km} onChange={event => change('mileage_km', event.target.value)} /></Field>
            <Field>VIN<Input value={form.vin} onChange={event => change('vin', event.target.value)} /></Field>
            <Field>Детские удерживающие устройства
              <CheckboxRow><input type="checkbox" checked={form.has_child_seat} onChange={event => change('has_child_seat', event.target.checked)} /> Детское кресло</CheckboxRow>
              <CheckboxRow><input type="checkbox" checked={form.has_booster} onChange={event => change('has_booster', event.target.checked)} /> Бустер</CheckboxRow>
            </Field>
          </FieldGrid>
          <SpecsEditor value={form.specs} onChange={value => change('specs', value)} />
          <Field style={{marginTop: 16}}>Описание<Textarea value={form.description || ''} onChange={event => change('description', event.target.value)} /></Field>
          <Field style={{marginTop: 16}}>
            Фотографии — по одному URL на строку
            <Textarea value={form.photos_text} onChange={event => change('photos_text', event.target.value)} placeholder="https://…/car-front.jpg" />
          </Field>
          {error && <ErrorText role="alert">{error}</ErrorText>}
          {message && <SuccessText aria-live="polite">{message}</SuccessText>}
          <Actions>
            <ActionButton type="submit" primary disabled={saving}>{saving ? 'Сохраняем…' : 'Сохранить автомобиль'}</ActionButton>
            {selectedId && <ActionButton type="button" onClick={startCreate}>Отменить редактирование</ActionButton>}
          </Actions>
        </form>

        {selectedId && <VehicleTelematics key={selectedId} authorization={authorization} vehicleId={selectedId} compact />}
        {selectedId && <VehicleDocuments authorization={authorization} vehicleId={selectedId} />}
      </Card>
    </Grid>
  );
}

const vehicleDocumentKinds = {
  sts: 'СТС',
  osago: 'ОСАГО',
  kasko: 'КАСКО',
  other: 'Другое',
};

/**
 * Документы автомобиля загружаются один раз и видны клиенту в каждой аренде
 * этой машины: перезаливать один и тот же ОСАГО на каждую бронь незачем.
 */
function VehicleDocuments({authorization, vehicleId}) {
  const [documents, setDocuments] = React.useState([]);
  const [form, setForm] = React.useState({kind: 'sts', title: '', valid_until: '', file: null});
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');

  const load = React.useCallback(() => (
    getVehicleDocuments(authorization, vehicleId)
      .then(data => setDocuments(Array.isArray(data) ? data : []))
      .catch(() => setDocuments([]))
  ), [authorization, vehicleId]);

  React.useEffect(() => { load(); }, [load]);

  const upload = async event => {
    event.preventDefault();
    if (!form.file || !form.title.trim()) {
      setError('Выберите файл и укажите название документа.');
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await uploadVehicleDocument(authorization, vehicleId, {
        kind: form.kind,
        title: form.title.trim(),
        valid_until: form.valid_until,
      }, form.file);
      setForm({kind: 'sts', title: '', valid_until: '', file: null});
      setMessage('Документ загружен и виден клиенту в его арендах.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async document => {
    const reason = window.prompt(`Основание для удаления «${document.title}»:`);
    if (!reason || !reason.trim()) { return; }
    setBusy(true);
    setError('');
    try {
      await deleteVehicleDocument(authorization, vehicleId, document.id, reason.trim());
      setMessage('Документ удалён.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DocumentsSection>
      <CardTitle>Документы автомобиля</CardTitle>
      {documents.length === 0 && <State>Документов пока нет. Клиент не увидит СТС и ОСАГО.</State>}
      {documents.length > 0 && (
        <List>
          {documents.map(document => (
            <DocumentRow key={document.id}>
              <div>
                <strong>{vehicleDocumentKinds[document.kind] || document.kind}</strong>
                {' · '}{document.title}
                <DocumentMeta>
                  {document.valid_until ? `действует до ${document.valid_until}` : 'без срока'}
                  {document.visible_to_client ? ' · виден клиенту' : ' · внутренний'}
                </DocumentMeta>
              </div>
              <ActionButton type="button" disabled={busy} onClick={() => remove(document)}>
                Удалить
              </ActionButton>
            </DocumentRow>
          ))}
        </List>
      )}

      <form onSubmit={upload}>
        <FieldGrid style={{marginTop: 16}}>
          <Field>Тип
            <Select value={form.kind} onChange={event => setForm({...form, kind: event.target.value})}>
              {Object.keys(vehicleDocumentKinds).map(kind => (
                <option key={kind} value={kind}>{vehicleDocumentKinds[kind]}</option>
              ))}
            </Select>
          </Field>
          <Field>Название *
            <Input
              value={form.title}
              placeholder="ОСАГО ХХХ 0123456789"
              onChange={event => setForm({...form, title: event.target.value})}
            />
          </Field>
          <Field>Действует до
            <Input
              type="date"
              value={form.valid_until}
              onChange={event => setForm({...form, valid_until: event.target.value})}
            />
          </Field>
          <Field>Файл (JPEG, PNG или PDF) *
            <Input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              onChange={event => setForm({...form, file: event.target.files[0] || null})}
            />
          </Field>
        </FieldGrid>
        {error && <ErrorText role="alert">{error}</ErrorText>}
        {message && <SuccessText aria-live="polite">{message}</SuccessText>}
        <Actions>
          <ActionButton type="submit" disabled={busy || !form.file || !form.title.trim()}>
            Загрузить документ
          </ActionButton>
        </Actions>
      </form>
    </DocumentsSection>
  );
}

export default function Fleet() {
  return (
    <Layout
      title="Автопарк"
      description="Цена хранится в копейках на сервере; в форме она показана в рублях. Занятые состояния меняются жизненным циклом аренды."
    >
      <FleetContent />
    </Layout>
  );
}

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  h2 { margin-bottom: 0; }
  button { min-width: auto; }
  margin-bottom: 16px;
`;

const VehicleHeading = styled.div`font-weight: 700;`;
const VehicleMeta = styled.div`margin: 5px 0 8px; color: #666b73; font-size: 12px;`;

const MapBox = styled.div`
  height: 320px;
  border: 1px solid #cad0d9;
  border-radius: 8px;
  overflow: hidden;
`;

const Divider = styled.div`
  margin: 20px 0;
  border-top: 1px solid #e6eaed;
`;

const MapHelp = styled.div`
  margin-top: 8px;
  color: #818286;
  font-size: 12px;
`;

const DocumentsSection = styled.section`
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #e6eaed;
`;

const DocumentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border: 1px solid #e6eaed;
  border-radius: 7px;
  color: #2f3033;
  font-size: 13px;
`;

const DocumentMeta = styled.div`
  margin-top: 4px;
  color: #818286;
  font-size: 12px;
`;
